import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import Graph from 'graphology';
import Sigma from 'sigma';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import { useNetworkGraph } from '../../hooks/useApi';
import type { Participant } from '../../party';

interface NetworkGraphProps {
  onSelectPerson: (slug: string) => void;
  selectedSlug: string | null;
  participants: Record<string, Participant>;
}

const CATEGORY_COLORS: Record<string, string> = {
  politician: '#ED4245',
  business: '#3BA55C',
  royalty: '#FAA61A',
  celebrity: '#5865F2',
  associate: '#EB459E',
  legal: '#9B59B6',
  academic: '#1ABC9C',
  socialite: '#E67E22',
  'military-intelligence': '#2C3E50',
  other: '#95A5A6',
};

const CATEGORY_LABELS: Record<string, string> = {
  politician: 'Politicians',
  business: 'Business',
  royalty: 'Royalty',
  celebrity: 'Celebrities',
  associate: 'Associates',
  legal: 'Legal',
  academic: 'Academics',
  socialite: 'Socialites',
  'military-intelligence': 'Intel / Military',
  other: 'Other',
};

/** Convert hex color to rgba string */
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function NetworkGraph({ onSelectPerson, selectedSlug, participants }: NetworkGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sigmaRef = useRef<Sigma | null>(null);
  const graphRef = useRef<Graph | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [minStrength, setMinStrength] = useState(20);
  const [searchHighlight, setSearchHighlight] = useState('');
  const [categoryFilters, setCategoryFilters] = useState<Set<string>>(new Set());
  const [nodeCount, setNodeCount] = useState(0);
  const [edgeCount, setEdgeCount] = useState(0);

  const { data: graphData, isLoading, error } = useNetworkGraph(minStrength);

  // Compute the top 10 nodes by degree for forced labels
  const topNodeIds = useRef<Set<string>>(new Set());

  // Build graph when data arrives
  useEffect(() => {
    if (!graphData?.data || !containerRef.current) return;

    const { nodes, edges } = graphData.data;
    const graph = new Graph();

    // Add nodes with widely spread random initial positions
    const spread = 5000;
    for (const node of nodes) {
      if (categoryFilters.size > 0 && !categoryFilters.has(node.category)) continue;

      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * spread;
      graph.addNode(node.id, {
        label: node.name,
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
        size: 5,
        color: CATEGORY_COLORS[node.category] || '#95A5A6',
        category: node.category,
        slug: node.slug,
      });
    }

    // Add edges with category-tinted colors
    for (const edge of edges) {
      if (!graph.hasNode(edge.source) || !graph.hasNode(edge.target)) continue;
      try {
        const sourceColor = graph.getNodeAttribute(edge.source, 'color') || '#95A5A6';
        const alpha = edge.strength === 'strong' ? 0.15 : edge.strength === 'medium' ? 0.10 : 0.08;
        const edgeSize = Math.max(0.3, Math.min(3, (edge.co_documents || 1) / 20 + 0.3));

        graph.addEdge(edge.source, edge.target, {
          size: edgeSize,
          color: hexToRgba(sourceColor, alpha),
          type: 'line',
          co_documents: edge.co_documents || 0,
          co_flights: edge.co_flights || 0,
          relationship_type: edge.relationship_type || '',
        });
      } catch {
        /* skip duplicate edges */
      }
    }

    // Scale node sizes by degree (min 3, max 30)
    const degrees: { node: string; degree: number }[] = [];
    graph.forEachNode((node) => {
      const degree = graph.degree(node);
      degrees.push({ node, degree });
      const scaledSize = Math.max(3, Math.min(30, 3 + Math.pow(degree, 0.65) * 1.8));
      graph.setNodeAttribute(node, 'size', scaledSize);
    });

    // Find top 10 nodes by degree and force their labels
    degrees.sort((a, b) => b.degree - a.degree);
    const top10 = new Set(degrees.slice(0, 10).map((d) => d.node));
    topNodeIds.current = top10;
    for (const nodeId of top10) {
      graph.setNodeAttribute(nodeId, 'forceLabel', true);
    }

    // Run ForceAtlas2 layout with inferred + tuned settings
    const inferredSettings = forceAtlas2.inferSettings(graph);
    forceAtlas2.assign(graph, {
      iterations: 400,
      settings: {
        ...inferredSettings,
        gravity: 0.05,
        scalingRatio: inferredSettings.scalingRatio ? inferredSettings.scalingRatio * 8 : 80,
        barnesHutOptimize: true,
        barnesHutTheta: 0.5,
        slowDown: 3,
        strongGravityMode: false,
        adjustSizes: true,
        outboundAttractionDistribution: true,
      },
    });

    graphRef.current = graph;
    setNodeCount(graph.order);
    setEdgeCount(graph.size);

    // Destroy previous sigma instance
    if (sigmaRef.current) {
      sigmaRef.current.kill();
      sigmaRef.current = null;
    }

    // Create Sigma renderer with optimized settings
    const sigma = new Sigma(graph, containerRef.current, {
      renderLabels: true,
      labelRenderedSizeThreshold: 12,
      labelDensity: 0.7,
      labelSize: 13,
      labelWeight: 'bold',
      labelColor: { color: '#e2e8f0' },
      labelFont: 'Inter, system-ui, sans-serif',
      hideLabelsOnMove: true,
      hideEdgesOnMove: true,
      defaultEdgeType: 'line',
      defaultNodeColor: '#95A5A6',
      stagePadding: 50,
      zIndex: true,
      minCameraRatio: 0.05,
      maxCameraRatio: 20,
      nodeReducer(node, data) {
        const res = { ...data };

        // Highlight selected node
        if (selectedSlug && data.slug === selectedSlug) {
          res.size = (data.size || 6) * 1.6;
          res.highlighted = true;
          res.zIndex = 10;
        }

        // Search highlight
        if (searchHighlight && data.label?.toLowerCase().includes(searchHighlight.toLowerCase())) {
          res.color = '#FFFFFF';
          res.size = (data.size || 6) * 1.4;
          res.zIndex = 5;
          res.forceLabel = true;
        }

        // Hover: dim non-neighbors
        if (hoveredNode && node !== hoveredNode) {
          const isNeighbor = graph.hasEdge(hoveredNode, node) || graph.hasEdge(node, hoveredNode);
          if (!isNeighbor) {
            res.color = hexToRgba(data.color || '#95A5A6', 0.08);
            res.label = '';
            res.zIndex = -1;
          } else {
            // Highlight neighbors
            res.size = (data.size || 6) * 1.2;
            res.forceLabel = true;
            res.zIndex = 5;
          }
        }

        // Hovered node itself
        if (hoveredNode && node === hoveredNode) {
          res.size = (data.size || 6) * 1.5;
          res.highlighted = true;
          res.forceLabel = true;
          res.zIndex = 10;
        }

        // Show participant indicators
        for (const p of Object.values(participants)) {
          if (p.viewing?.type === 'person' && p.viewing.id === data.slug) {
            res.borderColor = p.color;
            res.size = (data.size || 6) * 1.2;
          }
        }

        return res;
      },
      edgeReducer(edge, data) {
        const res = { ...data };
        if (hoveredNode) {
          const [source, target] = graph.extremities(edge);
          if (source !== hoveredNode && target !== hoveredNode) {
            res.hidden = true;
          } else {
            // Highlighted neighbor edge: brighter, thicker
            const sourceColor = graph.getNodeAttribute(source, 'color') || '#95A5A6';
            res.color = hexToRgba(sourceColor, 0.5);
            res.size = Math.max((data.size || 1) * 2, 1.5);
            res.zIndex = 5;
          }
        }
        return res;
      },
    });

    // Click handler
    sigma.on('clickNode', ({ node }) => {
      const attrs = graph.getNodeAttributes(node);
      if (attrs.slug) onSelectPerson(attrs.slug);
    });

    // Hover handlers
    sigma.on('enterNode', ({ node }) => setHoveredNode(node));
    sigma.on('leaveNode', () => setHoveredNode(null));

    // Double-click to select and expand
    sigma.on('doubleClickNode', ({ node }) => {
      const attrs = graph.getNodeAttributes(node);
      if (attrs.slug) {
        onSelectPerson(attrs.slug);
      }
    });

    sigmaRef.current = sigma;

    return () => {
      sigma.kill();
      sigmaRef.current = null;
    };
  }, [graphData, categoryFilters, minStrength]);

  // Refresh rendering when state changes (hover, selection, search, participants)
  useEffect(() => {
    sigmaRef.current?.refresh();
  }, [hoveredNode, selectedSlug, searchHighlight, participants]);

  const toggleCategory = useCallback((cat: string) => {
    setCategoryFilters((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  const resetZoom = useCallback(() => {
    if (sigmaRef.current) {
      const camera = sigmaRef.current.getCamera();
      camera.animatedReset({ duration: 300 });
    }
  }, []);

  const activeCategories = useMemo(() => {
    return Object.keys(CATEGORY_COLORS).filter(
      (cat) => categoryFilters.size === 0 || categoryFilters.has(cat)
    );
  }, [categoryFilters]);

  if (error) {
    return (
      <div className="view-error">
        <p>Failed to load network graph</p>
        <p className="error-detail">{String(error)}</p>
      </div>
    );
  }

  return (
    <div className="network-graph-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Controls panel - glassmorphism */}
      <div
        className="graph-controls"
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 10,
          background: 'rgba(15, 20, 30, 0.75)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: 12,
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          minWidth: 220,
          maxWidth: 280,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        }}
      >
        {/* Strength slider */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.5)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Min shared docs
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="range"
              min={1}
              max={100}
              value={minStrength}
              onChange={(e) => setMinStrength(Number(e.target.value))}
              style={{
                flex: 1,
                accentColor: '#5865F2',
                height: 4,
                cursor: 'pointer',
              }}
            />
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#e2e8f0',
                minWidth: 28,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {minStrength}
            </span>
          </div>
        </div>

        {/* Search */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.5)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Search
          </label>
          <input
            type="text"
            placeholder="Highlight person..."
            value={searchHighlight}
            onChange={(e) => setSearchHighlight(e.target.value)}
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 6,
              padding: '6px 10px',
              fontSize: 13,
              color: '#e2e8f0',
              outline: 'none',
              width: '100%',
            }}
          />
        </div>

        {/* Category filters */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.5)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Categories
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {Object.entries(CATEGORY_COLORS).map(([cat, color]) => {
              const isActive = categoryFilters.size === 0 || categoryFilters.has(cat);
              return (
                <button
                  key={cat}
                  onClick={() => toggleCategory(cat)}
                  title={CATEGORY_LABELS[cat] || cat}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '3px 8px',
                    fontSize: 11,
                    fontWeight: 500,
                    color: isActive ? '#e2e8f0' : 'rgba(255, 255, 255, 0.25)',
                    background: isActive ? hexToRgba(color, 0.15) : 'transparent',
                    border: `1px solid ${isActive ? hexToRgba(color, 0.4) : 'rgba(255, 255, 255, 0.06)'}`,
                    borderRadius: 20,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: isActive ? color : 'rgba(255, 255, 255, 0.15)',
                      flexShrink: 0,
                    }}
                  />
                  {CATEGORY_LABELS[cat] || cat}
                </button>
              );
            })}
          </div>
        </div>

        {/* Reset Zoom button */}
        <button
          onClick={resetZoom}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            color: '#e2e8f0',
            background: 'rgba(88, 101, 242, 0.15)',
            border: '1px solid rgba(88, 101, 242, 0.3)',
            borderRadius: 6,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLButtonElement).style.background = 'rgba(88, 101, 242, 0.3)';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLButtonElement).style.background = 'rgba(88, 101, 242, 0.15)';
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="8" y1="11" x2="14" y2="11" />
            <line x1="11" y1="8" x2="11" y2="14" />
          </svg>
          Reset Zoom
        </button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20,
            background: 'rgba(10, 15, 25, 0.8)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              border: '3px solid rgba(88, 101, 242, 0.2)',
              borderTopColor: '#5865F2',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <p style={{ marginTop: 12, color: '#94a3b8', fontSize: 14, fontWeight: 500 }}>
            Building network graph...
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Sigma container */}
      <div
        ref={containerRef}
        className="sigma-container"
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          inset: 0,
        }}
      />

      {/* Bottom info bar */}
      {graphData?.data && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            zIndex: 10,
            background: 'rgba(15, 20, 30, 0.7)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            borderRadius: 8,
            padding: '6px 12px',
            fontSize: 12,
            color: 'rgba(255, 255, 255, 0.5)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{nodeCount.toLocaleString()}</span> nodes
          {' \u00B7 '}
          <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{edgeCount.toLocaleString()}</span> edges
        </div>
      )}

      {/* Hover tooltip */}
      {hoveredNode && graphRef.current && (
        <HoverTooltip graph={graphRef.current} nodeId={hoveredNode} />
      )}
    </div>
  );
}

/** Tooltip showing node details on hover */
function HoverTooltip({ graph, nodeId }: { graph: Graph; nodeId: string }) {
  const attrs = graph.getNodeAttributes(nodeId);
  const degree = graph.degree(nodeId);
  const category = attrs.category || 'other';
  const color = CATEGORY_COLORS[category] || '#95A5A6';

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 12,
        left: 12,
        zIndex: 10,
        background: 'rgba(15, 20, 30, 0.85)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: 10,
        padding: '10px 14px',
        minWidth: 180,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: color,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>
          {attrs.label}
        </span>
      </div>
      <div style={{ fontSize: 11, color: 'rgba(255, 255, 255, 0.45)', display: 'flex', gap: 12 }}>
        <span>{CATEGORY_LABELS[category] || category}</span>
        <span>{degree} connections</span>
      </div>
    </div>
  );
}
